import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPinned, ArrowLeft, Images, Sparkles, Camera } from "lucide-react";
import api, { API } from "../lib/api";
import MapView from "../components/MapView";
import { Spinner } from "../components/common";

/* Photo Map — every trip photo with GPS (EXIF) or AI (PhotoPulse) coordinates as a pin. */
export default function PhotoMap() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/album/map/all").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  const token = localStorage.getItem("token");
  const src = (u) => `${API}${u.replace(/^\/api/, "")}?auth=${token}`;

  const markers = (items || []).map((p) => ({
    lat: p.lat, lon: p.lng, radius: 7,
    color: p.source === "photopulse" ? "#7c3aed" : "#0047FF",
    popup: (
      <div className="w-44">
        <img src={src(p.url)} alt={p.title || "trip photo"} className="w-full h-24 object-cover mb-1.5" />
        {p.title && <div className="font-bold text-sm leading-tight">{p.title}</div>}
        {p.location && <div className="text-xs text-zinc-500">{p.location}</div>}
        {p.taken_at && <div className="text-xs text-zinc-500">{String(p.taken_at).slice(0, 10)}</div>}
        <Link to={`/trips/${p.trip_id}`} className="text-xs text-blue-700 underline">{p.trip_title || "Open trip"} →</Link>
      </div>
    ),
  }));

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="photo-map-page">
      <Link to="/travel-hub" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mb-4" data-testid="photo-map-back">
        <ArrowLeft className="w-4 h-4" /> My Travel Hub
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-2.5">
          <MapPinned className="w-8 h-8 text-primary" /> My Photo Map
        </h1>
        {items && <div className="text-sm text-muted-foreground" data-testid="photo-map-count">{items.length} located photo{items.length === 1 ? "" : "s"}</div>}
      </div>
      <div className="flex flex-wrap gap-4 text-xs mb-3" data-testid="photo-map-legend">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#0047FF]" /> <Camera className="w-3.5 h-3.5" /> GPS from photo (EXIF)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#7c3aed]" /> <Sparkles className="w-3.5 h-3.5" /> Located by PhotoPulse AI</span>
      </div>
      {items === null ? <Spinner /> : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 bg-zinc-50 p-10 text-center" data-testid="photo-map-empty">
          <Images className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
          <p className="font-heading font-700 text-lg">No located photos yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Upload photos to a trip's album — GPS is read automatically from the photo, and PhotoPulse AI
            locates photos that have no GPS data.
          </p>
          <Link to="/travel-hub" className="inline-block mt-4 px-5 py-2.5 bg-primary text-white text-sm font-700 hover:bg-blue-700">Open My Travel Hub</Link>
        </div>
      ) : (
        <MapView markers={markers} height="600px" zoom={2} />
      )}
    </div>
  );
}
